/**
 * Wellbore geometry editing for stories 7 and 8 (INS-11588 / INS-11915). Every address
 * is `/well_workspaces/:id/wellbores/:uwi14/...`, with the geometry workspace resolved
 * behind it. Responses are camelCase, request bodies snake_case.
 */

import type { PreDrillGeometryStatus } from './pre-drill-workspace';

/** `{ href, auth }`, where `auth` is the server's list of permitted operations. It is
 *  the authority on whether a control is available; a rule re-derived here disagrees. */
export interface WellboreGeometryResource {
  href: string;
  auth: string[];
}

/** What a depth is measured from. Null when neither elevation resolves. */
export type WellboreDatumReference = 'kb' | 'ground_level';

/** These two stories only ever write shl, lp, bhl and unassigned. */
export type WellborePointType =
  'shl' | 'kop' | 'lp' | 'ftp' | 'ltp' | 'bhl' | 'unassigned';

/** Where the point itself came from. */
export type WellborePointSource =
  | 'vendor'
  | 'auto_provisioned'
  | 'user_derived'
  | 'promoted_geometry'
  | 'survey_station'
  | 'imported_survey';

/** `user` means an operator typed the depth, which makes the point an anchor the
 *  re-derivation interpolates around rather than overwrites. */
export type WellborePointDepthSource = 'derived' | 'user' | 'imported';

export interface WellborePoint {
  /** Stable across a re-derive; the key for the point writes. Null on the seed an
   *  untouched bore reads, which is written through a geometry edit instead. */
  id: number | null;
  /** Zero-based, dense, surface down. */
  positionalIndex: number;
  /** The stored name when set, otherwise the upcased point type. */
  name: string;
  latitude: number;
  longitude: number;
  /** Derived from the whole path, never from one point. Read-only. */
  measuredDepthFt: number | null;
  /** The only depth a write may set. */
  tvdFt: number | null;
  /** `tvdFt` seen through the datum. Derived, never accepted on a write. */
  tvdssFt: number | null;
  pointType: WellborePointType;
  source: WellborePointSource;
  depthSource: WellborePointDepthSource;
  /** The position is pinned; a write that moves it is refused. */
  locationLocked: boolean;
  /** Independent of the location lock: a pinned position can still take a depth. */
  depthLocked: boolean;
  /** Null is unverified, a third state rather than a failure. */
  isVerified: boolean | null;
  /** Permanently false on the surface hole and the bottom hole. */
  deletable: boolean;
}

export interface WellborePointsResponse {
  /** The geometry workspace resolved behind the address. Informational; no call takes it. */
  workspaceId: number;
  uwi14: string;
  /** False once the workspace is promoted, and every write then fails. */
  editable: boolean;
  /** Why editing is off. Null while editable. */
  lockReason: string | null;
  /** Where the derive stands: re-read until it leaves `processing`. `failed` on an
   *  edit's answer means the points were saved but no path could be built. */
  geometryStatus: PreDrillGeometryStatus;
  /** An input changed after the last successful derive, so the stored path no longer
   *  reflects the control set. */
  geometryStale: boolean;
  datumReference: WellboreDatumReference | null;
  datumElevationFt: number | null;
  /** True when the kelly bushing is derived rather than entered. */
  kbElevationEstimated: boolean;
  groundLevelFt: number | null;
  /** The DEM reading itself, beside the resolved value, so the drawer can show what was
   *  sampled next to what was entered. Identical to `groundLevelFt` until an override exists. */
  demGroundLevelFt: number | null;
  /** Kelly bushing minus ground level. Derived, read-only. */
  estimatedRigHeightFt: number | null;
  /** One index over the set rather than a flag per member. Null when none is elected. */
  landingPointIndex: number | null;
  /** The station the first step of 80 degrees or more starts from (the lateral's start), where
   *  the LP reads while none is elected (UAT item 10). Null when no step reaches it or depths
   *  are not derived yet. */
  firstHorizontalIndex: number | null;
  /** Ordered, surface hole first and bottom hole last. */
  points: WellborePoint[];
  resources: {
    self: WellboreGeometryResource;
    geometryEdits: WellboreGeometryResource;
    geometryRefreshes: WellboreGeometryResource;
    intersectedGrids: WellboreGeometryResource;
    /** `create` while the workspace is editable; the datum write (INS-12477). Absent on
     *  a backend that predates it, and the drawer then reads only. */
    datumUpdates?: WellboreGeometryResource;
  };
}

/** Why a grid carries no range, or why no indicator could be computed at all. */
export type IntersectedGridReason =
  | 'missing_geometry'
  | 'missing_permitted_depth_tvd_ft'
  | 'missing_datum'
  | 'outside_grid_extent'
  | 'no_grid_value_along_bore';

export interface IntersectedGrid {
  structureGridId: number;
  geologicIntervalId: number | null;
  geologicInterval: string | null;
  /** Stratigraphic order: sort on this and the list reads top down. */
  positionalIndex: number | null;
  displayColor: string | null;
  /** Whether this grid's raster covers the bore's ground track, in plan view. */
  gridCoversBoreTrack: boolean;
  /** The band this interval occupies under this well. Null only when it has no range here. */
  minTvdssFt: number | null;
  maxTvdssFt: number | null;
  /** Whether the current geometry enters the interval. Null when it carries no depth.
   *  A separate 3D fact: it never suppresses a range or the indicator. */
  boreEntersInterval: boolean | null;
  /** The indicator. Null when the response is not applicable or the grid has no range.
   *  More than one grid may be true where a horizon crosses the permitted depth. */
  containsPermittedDepth: boolean | null;
  reason: IntersectedGridReason | null;
}

export interface IntersectedGridsResponse {
  workspaceId: number;
  uwi14: string;
  structureGridCollectionId: number | null;
  datumReference: WellboreDatumReference | null;
  datumElevationFt: number | null;
  groundLevelFt: number | null;
  permittedDepthTvdFt: number | null;
  /** The permitted depth expressed subsea, the space the grid samples live in. */
  permitFloorTvdssFt: number | null;
  /** Whether an indicator could be computed at all. Ranges come back either way. */
  applicable: boolean;
  reason: IntersectedGridReason | null;
  /** Every grid in the collection, including those with no range under this well. */
  grids: IntersectedGrid[];
  resources: {
    self: WellboreGeometryResource;
    geometryRefreshes: WellboreGeometryResource;
  };
}

/** One member of a geometry edit. Absent `tvd_ft` hands the point to the derivation. */
export interface WellboreGeometryEditPoint {
  /** The row this point came from, so the save carries its depth and provenance
   *  forward; omitted for a point added on the map. Ids change on every save. */
  id?: number;
  latitude: number;
  longitude: number;
  tvd_ft?: number | null;
  point_type?: WellborePointType;
  location_locked?: boolean;
}

// URL builders: every address is built from the Well Workspace id and the UWI14, in
// place of hrefs the workspace read does not carry.

/**
 * The base address for one bore's geometry inside a Well Workspace.
 * @param {number} wellWorkspaceId The Well Workspace.
 * @param {string} uwi14 The bore.
 * @return {string} The base path, without a trailing slash.
 */
export const wellboreGeometryBase = (
  wellWorkspaceId: number,
  uwi14: string
): string => `/well_workspaces/${wellWorkspaceId}/wellbores/${uwi14}`;

/**
 * The bore's ordered control set and the datum its depths are read through.
 * @param {number} wellWorkspaceId The Well Workspace.
 * @param {string} uwi14 The bore.
 * @return {string} The points path.
 */
export const wellborePointsPath = (
  wellWorkspaceId: number,
  uwi14: string
): string => `${wellboreGeometryBase(wellWorkspaceId, uwi14)}/points`;

/**
 * One point, for the synchronous edit and delete.
 * @param {number} wellWorkspaceId The Well Workspace.
 * @param {string} uwi14 The bore.
 * @param {number} pointId The point.
 * @return {string} The point path.
 */
export const wellborePointPath = (
  wellWorkspaceId: number,
  uwi14: string,
  pointId: number
): string => `${wellborePointsPath(wellWorkspaceId, uwi14)}/${pointId}`;

/**
 * Replaces the whole ordered set and re-derives the path.
 * @param {number} wellWorkspaceId The Well Workspace.
 * @param {string} uwi14 The bore.
 * @return {string} The geometry edits path.
 */
export const geometryEditsPath = (
  wellWorkspaceId: number,
  uwi14: string
): string => `${wellboreGeometryBase(wellWorkspaceId, uwi14)}/geometry_edits`;

/**
 * Re-derives depths from the grid or a typed Target TVD.
 * @param {number} wellWorkspaceId The Well Workspace.
 * @param {string} uwi14 The bore.
 * @return {string} The geometry refreshes path.
 */
export const geometryRefreshesPath = (
  wellWorkspaceId: number,
  uwi14: string
): string =>
  `${wellboreGeometryBase(wellWorkspaceId, uwi14)}/geometry_refreshes`;

/**
 * The grids in the workspace's collection with their bands under this bore.
 * @param {number} wellWorkspaceId The Well Workspace.
 * @param {string} uwi14 The bore.
 * @return {string} The intersected grids path.
 */
export const intersectedGridsPath = (
  wellWorkspaceId: number,
  uwi14: string
): string =>
  `${wellboreGeometryBase(wellWorkspaceId, uwi14)}/intersected_grids`;

/**
 * Sets the ground level and the kelly bushing every depth is measured from; answers
 * with the whole points payload. The rig height is derived on read.
 * @param {number} wellWorkspaceId The Well Workspace.
 * @param {string} uwi14 The bore.
 * @return {string} The datum updates path.
 */
export const datumUpdatesPath = (
  wellWorkspaceId: number,
  uwi14: string
): string => `${wellboreGeometryBase(wellWorkspaceId, uwi14)}/datum_updates`;

/**
 * The target-zone reads and writes, addressed under the bore when the workspace is
 * opened from a Well Workspace. Same payloads as the workspace-addressed endpoints
 * the Well page's own workspace keeps using; only the address differs.
 * @param {number} wellWorkspaceId The Well Workspace.
 * @param {string} uwi14 The bore.
 * @return {string} The path.
 */
export const targetZoneSuggestionsPath = (
  wellWorkspaceId: number,
  uwi14: string
): string =>
  `${wellboreGeometryBase(wellWorkspaceId, uwi14)}/target_zone_suggestions`;

export const targetZoneRefreshesPath = (
  wellWorkspaceId: number,
  uwi14: string
): string =>
  `${wellboreGeometryBase(wellWorkspaceId, uwi14)}/target_zone_refreshes`;

export const targetZoneConfirmationsPath = (
  wellWorkspaceId: number,
  uwi14: string
): string =>
  `${wellboreGeometryBase(wellWorkspaceId, uwi14)}/target_zone_confirmations`;

/**
 * Mark as Current for the Well Workspace's own geometry on the bore: makes it the
 * source every wells listing drives the bore from. Only that row lives at this
 * address; another workspace's geometry is still selected through its own href.
 * @param {number} wellWorkspaceId The Well Workspace.
 * @param {string} uwi14 The bore.
 * @return {string} The path.
 */
export const defaultSelectionsPath = (
  wellWorkspaceId: number,
  uwi14: string
): string =>
  `${wellboreGeometryBase(wellWorkspaceId, uwi14)}/default_selections`;

/** The datum write. Either field may be omitted: an absent one is left as it is, so a
 *  ground level can be saved without restating the kelly bushing. */
export interface WellboreDatumUpdate {
  ground_elevation_ft?: number;
  datum_elevation_ft?: number;
}

/**
 * What the point editor sends; every field is optional. `tvdss_ft` is converted back
 * through the datum, and `tvd_ft` wins when both arrive.
 */
export interface WellborePointEdit {
  latitude?: number;
  longitude?: number;
  tvd_ft?: number | null;
  tvdss_ft?: number | null;
  clear_typed_depth?: boolean;
}
